import { useState, useCallback, useEffect, useRef, useMemo } from 'react'

const SCROLL_THRESHOLD = window.innerHeight / 2
const MIN_ITEMS_COUNT = 8
const THROTTLE_DELAY = 200
function throttle<T extends (...args: any[]) => void>(
  func: T,
  delay: number,
): T & { cancel: () => void } {
  let timeoutId: ReturnType<typeof setTimeout> | null = null
  let lastExecutedTime = 0

  const throttled = function (this: any, ...args: Parameters<T>) {
    const now = Date.now()
    const timeSinceLastExecution = now - lastExecutedTime

    const execute = () => {
      lastExecutedTime = now
      func.apply(this, args)
    }

    if (timeSinceLastExecution >= delay) {
      execute()
    } else {
      if (timeoutId) clearTimeout(timeoutId)
      timeoutId = setTimeout(execute, delay - timeSinceLastExecution)
    }
  } as T & { cancel: () => void }

  throttled.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
  }

  return throttled
}

interface UseScrollToTopOptions {
  scrollThreshold?: number
  minItemsCount?: number
  throttleDelay?: number
  scrollContainerSelector?: string
}

interface UseScrollToTopReturn {
  showBackToTop: boolean
  scrollToTop: () => void
  handleScroll: (event: Event | number) => void
  scrollElementRef: React.RefObject<HTMLElement | null>
}
export const useScrollToTop = (
  itemsCount: number = 0,
  options: UseScrollToTopOptions = {},
): UseScrollToTopReturn => {
  const {
    scrollThreshold = SCROLL_THRESHOLD,
    minItemsCount = MIN_ITEMS_COUNT,
    throttleDelay = THROTTLE_DELAY,
    scrollContainerSelector = '.pullToRefresh',
  } = options

  const [showBackToTop, setShowBackToTop] = useState(false)
  const backToTopIsVisible = useRef(false)
  const scrollElementRef = useRef<HTMLElement | null>(null)

  const handleScrollInternal = useMemo(
    () =>
      throttle((scrollTop: number) => {
        const shouldShow =
          scrollTop > scrollThreshold && itemsCount > minItemsCount

        if (shouldShow !== backToTopIsVisible.current) {
          backToTopIsVisible.current = shouldShow
          setShowBackToTop(shouldShow)
        }
      }, throttleDelay),
    [itemsCount, scrollThreshold, minItemsCount, throttleDelay],
  )

  // Wrapper for handleScroll that can accept Event or number
  const handleScroll = useCallback(
    (eventOrScrollTop: Event | number) => {
      if (typeof eventOrScrollTop === 'number') {
        handleScrollInternal(eventOrScrollTop)
      } else {
        const target = eventOrScrollTop.target as HTMLElement
        if (target) {
          handleScrollInternal(target.scrollTop)
        }
      }
    },
    [handleScrollInternal],
  )

  useEffect(() => {
    const scrollContainer = document.querySelector(scrollContainerSelector)

    const onScroll = () => {
      if (scrollContainer) {
        handleScrollInternal(scrollContainer.scrollTop)
      }
    }

    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', onScroll)
      return () => {
        scrollContainer.removeEventListener('scroll', onScroll)
        handleScrollInternal.cancel() // Cancel throttle on unmount
      }
    }
  }, [handleScrollInternal, scrollContainerSelector])

  // Scroll to top function - same as in the RN version
  const scrollToTop = useCallback(() => {
    const scrollContainer = document.querySelector(scrollContainerSelector)
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [scrollContainerSelector])

  // Store reference to scroll container in ref
  useEffect(() => {
    const scrollContainer = document.querySelector(
      scrollContainerSelector,
    ) as HTMLElement | null
    scrollElementRef.current = scrollContainer
  }, [scrollContainerSelector])

  return {
    showBackToTop,
    scrollToTop,
    handleScroll,
    scrollElementRef,
  }
}
